import type { BackendClient } from '@playbooks/core/src/services/BackendClient'
import {
  AiTeammateRealtimeModel,
  AiTeammateTranscriptionLanguage,
  Section,
} from '@playbooks/core/src/interfaces/ActionCard'
import type { SpeakerLabel, Utterance } from '../audio/utterance'
import type { UtteranceFeed } from '../audio/utteranceFeed'
import { RealtimeStatsCollector, type RealtimeUsage } from './realtimeStatsCollector'

/**
 * Events for the widget's transcript and playbook filling.
 * Most events come from OpenAI. A speaker session also adds its own events (see {@link UtteranceEventType}),
 * and adds the speaker, the utterance, and the connection to the events (see `speaker`, `utterance`, `connectionId`).
 */
export interface TeammateRealtimeEvent {
  type: string
  response?: {
    id?: string
    usage?: RealtimeUsage
    output?: Array<{
      type?: string
      name?: string
      arguments?: string
    }>
  }
  /** Conversation item of transcription events */
  item_id?: string
  /** Speaker of the item: the speaker channel of this session. Added by a speaker session. */
  speaker?: SpeakerLabel
  /**
   * The utterance of the item, with its position in the captured audio. Approximate, because WebRTC timing varies.
   * Added by a speaker session, once OpenAI confirms the commit.
   */
  utterance?: Utterance
  /**
   * The OpenAI connection of the event. Two sessions, or a session after a reconnect, can reuse item IDs.
   * Added by a speaker session.
   */
  connectionId?: string
  /** Transcription delta text */
  delta?: string
  transcript?: string
  usage?: RealtimeUsage
  session?: {
    audio?: { input?: { turn_detection?: unknown } }
  }
  error?: {
    type?: string
    code?: string
    message?: string
  }
}

/**
 * Events that a speaker session adds to the OpenAI events.
 * `started`: speech started. The transcript reserves the place of the coming text.
 * `empty`: OpenAI rejected a commit without audio. No text will come for this utterance.
 */
export const UtteranceEventType = {
  started: 'teammate.utterance.started',
  empty: 'teammate.utterance.empty',
} as const

/**
 * Coarse connection state emitted to React so the component can render controls.
 */
export type TeammateRealtimeStatus = 'connecting' | 'listening' | 'stopped'

/**
 * Stable error codes mapped to localized widget strings by the React layer.
 */
export type TeammateRealtimeErrorCode = 'audioUnavailable' | 'connectionLost'

/**
 * Per-session OpenAI Realtime creation options sent to the backend.
 */
export interface TeammateRealtimeSessionConfig {
  /**
   * Request a transcription-only session, without playbook filling.
   * Speaker attribution uses one such session for the microphone and one for the system audio.
   */
  purpose?: 'transcript'
  actionCardSections: string[]
  currentDate?: string
  recordTranscript?: boolean
  transcriptionLanguage?: AiTeammateTranscriptionLanguage
  fillAutomatically?: boolean
  model?: AiTeammateRealtimeModel
}

/**
 * Dependencies and callbacks required by {@link TeammateRealtimeController}.
 */
interface TeammateRealtimeControllerParams {
  backendClient: BackendClient
  recordTranscript?: boolean
  transcriptionLanguage?: AiTeammateTranscriptionLanguage
  fillAutomatically?: boolean
  model?: AiTeammateRealtimeModel
  getErrorMessage: (code: TeammateRealtimeErrorCode) => string
  onEvent: (event: TeammateRealtimeEvent) => void
  onStatusChange: (status: TeammateRealtimeStatus) => void
  onError: (message: string) => void
}

// Session will be recreated after this duration to proactively rotate before OpenAI's 60-minute limit
const SESSION_ROTATION_MS = 55 * 60 * 1000
const MAX_RECONNECT_ATTEMPTS = 5
const RECONNECT_BASE_DELAY_MS = 1000
const RECONNECT_MAX_DELAY_MS = 15000
/** Max time to wait for the final transcripts of committed speech when stopping */
const STOP_TRANSCRIPTION_TIMEOUT_MS = 3000
/**
 * Max time a session rotation waits for the end of the current utterance.
 * 55 minutes plus this wait stay below OpenAI's 60-minute limit.
 */
const ROTATION_MAX_WAIT_MS = 2 * 60 * 1000
/** Max time a speaker session waits until OpenAI confirms that the widget commits the utterances */
const SESSION_READY_TIMEOUT_MS = 10000

/** Events with the final transcript (or its failure) of a committed conversation item */
const finalTranscriptEvents = new Set([
  'conversation.item.input_audio_transcription.completed',
  'conversation.item.input_audio_transcription.failed',
])

/**
 * Read from a session event who detects the end of utterances.
 * `server`: OpenAI detects it. `manual`: the widget must commit each utterance.
 * Return `undefined` when the event does not include this setting. The current mode then stays.
 */
function getTurnDetection(event: TeammateRealtimeEvent): 'server' | 'manual' | undefined {
  const input = event.session?.audio?.input
  if (!input || !('turn_detection' in input)) return undefined
  return input.turn_detection ? 'server' : 'manual'
}

/**
 * Owns one OpenAI connection: for ordinary transcription, for one speaker (with speaker attribution),
 * or for filling the playbook.
 * The audio controller owns the media track sent to OpenAI. This controller reuses that track when it reconnects.
 *
 * Who decides where an utterance ends:
 * - With an `UtteranceFeed` (transcription): the widget's speech detection.
 *   This controller sends a commit to OpenAI after each utterance of the feed, when the session expects them.
 * - Without one (playbook filling): OpenAI itself (server turn detection).
 * A commit tells OpenAI that the utterance is complete. OpenAI then confirms it with an item ID,
 * and later returns the final text for that ID.
 *
 * A speaker session is a session whose feed has a speaker label (`me` or `remote`). Unlike ordinary sessions,
 * it requires manual commits, and it labels its events: it links each item ID to the utterance,
 * so the transcript can sort the texts of both speakers.
 *
 * It keeps browser connection objects, reconnect timers, session rotation, and stats collection outside React state.
 * React receives coarse status updates and parsed events.
 */
export class TeammateRealtimeController {
  /** Random ID of this controller. Two OpenAI sessions can return the same item IDs; this tells them apart. */
  private readonly controllerId = Math.random().toString(36).slice(2, 10)
  /** Active WebRTC peer connection for the current OpenAI Realtime session. */
  private peerConnection: RTCPeerConnection | null = null
  /** OpenAI Realtime event channel associated with the current peer connection. */
  private dataChannel: RTCDataChannel | null = null
  /** Audio stream currently being sent to OpenAI; retained so reconnect can reuse it. */
  private mediaStream: MediaStream | null = null
  /** Stats collector for the current internal backend session ID. */
  private statsCollector: RealtimeStatsCollector | null = null
  /** Timer that proactively rotates before OpenAI's 60-minute session limit. */
  private sessionRotationTimer: ReturnType<typeof setTimeout> | null = null
  /** Backoff timer for accidental disconnects or failed reconnect attempts. */
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null
  /** Monotonic token used to ignore events from stale peer connections. */
  private connectionGeneration = 0
  /** Number of consecutive reconnect attempts since the last successful connection. */
  private reconnectAttempts = 0
  /**
   * Desired high-level state: true while the user wants AI Teammate to keep listening.
   * This stays true across planned rotation and transient reconnects, unlike
   * `isConnecting`, which only describes the current WebRTC attempt.
   */
  private shouldKeepListening = false
  /**
   * Distinguishes user-initiated stops from unexpected WebRTC closes. Connection
   * close handlers use this to avoid scheduling reconnect after an intentional stop.
   */
  private isManualDisconnect = false
  /** True while an SDP exchange or reconnect attempt is already in progress. */
  private isConnecting = false
  /** Latest raw Action Card sections used to build the next backend session config. */
  private actionCardSections: Section[] = []
  /** Where the utterances of the sent audio end. Null when OpenAI detects them (playbook filling). */
  private utterances: UtteranceFeed | null = null
  /**
   * The committed utterances, in the order of the commits.
   * Each OpenAI commit confirmation, or rejection of an empty commit, takes the first entry.
   */
  private readonly committedUtterances: Utterance[] = []
  /** The utterances by OpenAI item ID, so the text events of an item get its utterance. */
  private readonly itemUtterances = new Map<string, Utterance>()
  /** Completes the start of a speaker session when OpenAI confirms that the widget commits the utterances. */
  private resolveSessionReady: ((ready: boolean) => void) | null = null
  /**
   * True if the session has no server turn detection (transcript-only sessions with streaming transcription models),
   * so utterances detected by the widget have to be committed by the client to receive completed transcripts.
   */
  private needsClientCommits = false
  /** Number of commits sent but not acknowledged yet (as committed, or rejected as empty) */
  private unacknowledgedCommits = 0
  /** Committed conversation items still waiting for their final transcript */
  private pendingTranscriptItemIds = new Set<string>()
  /** In-flight stop, shared by concurrent stop calls (e.g. a double click) */
  private stopPromise: Promise<void> | null = null
  /** Resolves the wait for the final transcripts when stopping */
  private checkPendingResults: (() => void) | null = null
  /** True when the session rotation waits for the end of the current utterance (see {@link requestRotation}). */
  private rotationDue = false
  /** Number of OpenAI responses (playbook filling) that started but did not finish yet */
  private activeResponses = 0

  /**
   * Create a controller with backend and UI callback dependencies.
   */
  constructor(private readonly params: TeammateRealtimeControllerParams) {}

  /**
   * Store the latest raw Action Card sections for future Realtime session creation.
   */
  setActionCardSections(actionCardSections: Section[]) {
    this.actionCardSections = actionCardSections
  }

  /**
   * Connect the given media track to a new OpenAI session.
   * `utterances` reports where the utterances of this audio end. Without it, OpenAI detects them.
   * With a speaker feed, this is a speaker session.
   * Its track stays muted until OpenAI confirms that the widget commits the utterances.
   */
  async start(stream: MediaStream, utterances?: UtteranceFeed) {
    // Let an in-flight stop finish first, so its delayed cleanup can't close the new session
    await this.stopPromise
    this.shouldKeepListening = true
    this.isManualDisconnect = false
    this.reconnectAttempts = 0
    this.mediaStream = stream
    this.utterances = utterances ?? null
    if (utterances) {
      utterances.onStart = utterance => {
        if (this.speaker) this.params.onEvent(this.labelSpeakerEvent({ type: UtteranceEventType.started }, utterance))
      }
      utterances.onCommit = utterance => {
        if (this.needsClientCommits) this.commitSpeech(utterance)
      }
    }
    await this.connectWebRTC(stream)
  }

  /**
   * Connect to OpenAI. For a speaker session, also wait until OpenAI confirms that the widget commits utterances.
   * OpenAI can first report its defaults (OpenAI detects the end of utterances) in `session.created`,
   * and confirm the requested setting in `session.updated`. Without this wait, the first words could be lost.
   * Report listening only after the confirmation. Fail after ten seconds without it.
   * A stop during the wait ends the start without an error.
   */
  private async connectWebRTC(stream: MediaStream) {
    if (!this.speaker) return this.connectWebRTCInternal(stream)
    const sessionReady = new Promise<boolean>(resolve => {
      this.resolveSessionReady = resolve
    })
    const timeout = setTimeout(() => this.resolveSessionReady?.(false), SESSION_READY_TIMEOUT_MS)
    try {
      await this.connectWebRTCInternal(stream)
      if (await sessionReady) {
        // The connection succeeded only now. A failed readiness check counts as a failed reconnect attempt.
        this.reconnectAttempts = 0
        this.params.onStatusChange('listening')
      } else if (this.shouldKeepListening) {
        this.cleanupConnection()
        throw new Error('OpenAI did not confirm manual commits for the transcript session.')
      }
    } finally {
      clearTimeout(timeout)
      this.resolveSessionReady = null
    }
  }

  /**
   * Stop listening, finalize stats, close WebRTC, and notify React that listening stopped.
   * Pending speech is transcribed first, so the last utterance before stopping is not lost.
   * Concurrent calls share the same stop.
   */
  async stop() {
    this.stopPromise ??= (async () => {
      await this.commitPendingSpeech()
      await this.stopInternal(true)
    })().finally(() => {
      this.stopPromise = null
    })
    await this.stopPromise
  }

  /**
   * Dispose resources without emitting React status updates.
   *
   * Use this from unmount paths where updating React state after cleanup would be unsafe.
   */
  async dispose() {
    await this.stopInternal(false)
  }

  /**
   * Return whether the controller is meant to keep listening across reconnects.
   */
  isActive() {
    return this.shouldKeepListening
  }

  private async stopInternal(emitStatus: boolean) {
    this.detachUtterances()
    this.shouldKeepListening = false
    this.isManualDisconnect = true
    this.isConnecting = false
    this.clearReconnectTimer()
    this.clearSessionRotationTimer()
    this.cleanupConnection()
    await this.stopStatsCollector()
    this.mediaStream = null
    if (emitStatus) {
      this.params.onStatusChange('stopped')
    }
  }

  private async connectWebRTCInternal(stream: MediaStream) {
    // Capture a generation for this connection attempt. Any later cleanup or
    // reconnect increments the shared generation and invalidates these handlers.
    const connectionGeneration = this.connectionGeneration + 1
    this.connectionGeneration = connectionGeneration
    this.isConnecting = true
    this.params.onStatusChange('connecting')

    const pc = new RTCPeerConnection()
    this.peerConnection = pc

    pc.addEventListener('connectionstatechange', () => {
      if (connectionGeneration !== this.connectionGeneration) return
      if (pc.connectionState === 'failed') {
        this.scheduleReconnect('connection failed')
      } else if (pc.connectionState === 'disconnected') {
        this.scheduleReconnect('connection disconnected')
      }
    })

    pc.addEventListener('iceconnectionstatechange', () => {
      if (connectionGeneration !== this.connectionGeneration) return
      if (pc.iceConnectionState === 'failed' || pc.iceConnectionState === 'disconnected') {
        this.scheduleReconnect(`ICE ${pc.iceConnectionState}`)
      }
    })

    stream.getTracks().forEach(track => {
      // A speaker session sends no audio until OpenAI confirms that the widget commits the utterances.
      if (this.speaker) track.enabled = false
      pc.addTrack(track, stream)
    })

    // OpenAI Realtime server events arrive over this data channel.
    const dataChannel = pc.createDataChannel('oai-events')
    this.dataChannel = dataChannel
    this.setupDataChannelListeners(dataChannel, connectionGeneration)

    // The backend owns OpenAI API credentials and exchanges our SDP offer for
    // OpenAI's SDP answer plus an internal stats session ID.
    const offer = await pc.createOffer()
    await pc.setLocalDescription(offer)

    const { sdpAnswer, sessionId } = await this.params.backendClient.createTeammateSession({
      sdp: offer.sdp!,
      ...this.buildSessionConfig(),
    })

    await pc.setRemoteDescription({ type: 'answer', sdp: sdpAnswer })

    // A newer connection superseded this one while the async SDP exchange was
    // in flight. Close this peer and leave the current connection untouched.
    if (connectionGeneration !== this.connectionGeneration) {
      pc.close()
      return
    }

    // Stats are per OpenAI Realtime session. Finalize the previous session
    // before recording usage events against the newly-created backend row.
    if (sessionId > 0) {
      await this.stopStatsCollector()
      this.statsCollector = new RealtimeStatsCollector(this.params.backendClient)
      this.statsCollector.start(sessionId)
    }

    // A speaker session resets the attempts only when it is ready (see `connectWebRTC`).
    if (!this.speaker) this.reconnectAttempts = 0
    this.clearReconnectTimer()
    this.startSessionRotationTimer()
    this.isConnecting = false
    if (!this.speaker) this.params.onStatusChange('listening')
  }

  private setupDataChannelListeners(dataChannel: RTCDataChannel, connectionGeneration: number) {
    dataChannel.onopen = () => {
      if (connectionGeneration !== this.connectionGeneration) return
      console.log('Data channel opened')
    }

    dataChannel.onclose = () => {
      if (connectionGeneration !== this.connectionGeneration) return
      console.log('Data channel closed')
      this.scheduleReconnect('data channel closed')
    }

    dataChannel.onerror = err => {
      if (connectionGeneration !== this.connectionGeneration) return
      console.error('Data channel error:', err)
      this.scheduleReconnect('data channel error')
    }

    dataChannel.onmessage = event => {
      if (connectionGeneration !== this.connectionGeneration) return
      try {
        const message = JSON.parse(event.data) as TeammateRealtimeEvent
        // Usage must be recorded before handing the event to business logic so
        // parsing/tool-call errors in React do not drop valid usage stats.
        this.statsCollector?.record(message)
        this.trackSpeechCommits(message)
        // Utterances come only from commit confirmations. Text that arrives earlier gets its utterance later.
        const utterance = message.item_id ? this.itemUtterances.get(message.item_id) : undefined
        this.params.onEvent(this.speaker ? this.labelSpeakerEvent(message, utterance) : message)
      } catch (err) {
        console.error('Error parsing message:', err)
      }
    }
  }

  /**
   * Track the commits in sessions without server turn detection: once the session turns out to have
   * no turn detection, each utterance detected by the widget is committed when it ends,
   * which makes OpenAI send its completed transcript.
   * Link each committed utterance to the item ID that OpenAI confirms for it.
   */
  private trackSpeechCommits(event: TeammateRealtimeEvent) {
    if (event.type === 'session.created' || event.type === 'session.updated') {
      this.handleTurnDetection(getTurnDetection(event))
      return
    }

    // Track the committed speech until its final transcript arrives
    if (event.type === 'input_audio_buffer.committed') {
      const utterance = this.committedUtterances.shift()
      if (utterance && event.item_id) this.itemUtterances.set(event.item_id, utterance)
      this.unacknowledgedCommits = Math.max(this.unacknowledgedCommits - 1, 0)
      // Only sessions that record a transcript receive final transcripts.
      if (event.item_id && this.params.recordTranscript) this.pendingTranscriptItemIds.add(event.item_id)
      // An utterance has ended: a good moment for a due session rotation.
      if (this.rotationDue) void this.rotateSession()
    } else if (event.type === 'response.created') {
      this.activeResponses++
    } else if (event.type === 'response.done') {
      this.activeResponses = Math.max(this.activeResponses - 1, 0)
    } else if (event.type === 'error' && event.error?.code === 'input_audio_buffer_commit_empty') {
      const utterance = this.committedUtterances.shift()
      if (utterance && this.speaker) {
        this.params.onEvent(this.labelSpeakerEvent({ type: UtteranceEventType.empty }, utterance))
      }
      this.unacknowledgedCommits = Math.max(this.unacknowledgedCommits - 1, 0)
    } else if (finalTranscriptEvents.has(event.type) && event.item_id) {
      this.pendingTranscriptItemIds.delete(event.item_id)
    }
    this.checkPendingResults?.()
  }

  /** Whether committed speech is still waiting for its final transcript */
  private hasPendingTranscripts() {
    return this.unacknowledgedCommits > 0 || this.pendingTranscriptItemIds.size > 0
  }

  /**
   * Apply who detects the end of utterances in the current session (see `getTurnDetection`).
   * With manual commits, the utterance feed starts reporting; with OpenAI's detection, it stops.
   * A speaker session also starts its audio when OpenAI confirms that the widget commits.
   * Before that, OpenAI's defaults keep it waiting.
   * If OpenAI later switches to its own detection, the speaker session reconnects.
   */
  private handleTurnDetection(turnDetection: 'server' | 'manual' | undefined) {
    if (!turnDetection) return
    const manual = turnDetection === 'manual'
    if (manual === this.needsClientCommits) return
    this.needsClientCommits = manual
    this.utterances?.setEnabled(manual)
    if (!this.speaker) return

    this.mediaStream?.getTracks().forEach(track => {
      track.enabled = manual
    })
    if (manual) {
      this.resolveSessionReady?.(true)
    } else {
      // The widget's commits would then fail, and this speaker would get no more text while it seems to listen.
      console.error('OpenAI enabled server turn detection in a speaker session')
      this.scheduleReconnect('server turn detection enabled')
    }
  }

  /** Stop the utterance feed, and remove this controller's callbacks. */
  private detachUtterances() {
    if (!this.utterances) return
    this.utterances.setEnabled(false)
    this.utterances.onCommit = null
    this.utterances.onStart = null
    this.utterances = null
  }

  /**
   * Commit the given utterance
   */
  private commitSpeech(utterance: Utterance) {
    if (this.dataChannel?.readyState === 'open') {
      this.committedUtterances.push(utterance)
      this.dataChannel.send(JSON.stringify({ type: 'input_audio_buffer.commit' }))
      this.unacknowledgedCommits++
    }
  }

  /**
   * Commit the pending utterance, if any, and wait (limited) for the final transcripts of all committed speech
   */
  private async commitPendingSpeech() {
    if (!this.needsClientCommits || this.dataChannel?.readyState !== 'open') return
    await this.utterances?.finish()
    await this.waitUntil(() => !this.hasPendingTranscripts())
  }

  /** Wait until `isDone` returns true, for at most three seconds. Each OpenAI event checks it again. */
  private async waitUntil(isDone: () => boolean) {
    if (isDone()) return
    await new Promise<void>(resolve => {
      const timeout = setTimeout(resolve, STOP_TRANSCRIPTION_TIMEOUT_MS)
      this.checkPendingResults = () => {
        if (!isDone()) return
        clearTimeout(timeout)
        resolve()
      }
    })
    this.checkPendingResults = null
  }

  /**
   * Prepare the session rotation before OpenAI's 60-minute limit.
   * Closing the session in the middle of an utterance would lose it: its audio is in the old session.
   * So the rotation waits until an utterance ends: until the next commit confirmation from OpenAI.
   * When no utterance ends within two minutes, for example in silence, the rotation happens anyway.
   */
  private requestRotation() {
    this.rotationDue = true
    this.sessionRotationTimer = setTimeout(() => void this.rotateSession(), ROTATION_MAX_WAIT_MS)
  }

  /**
   * Replace the OpenAI session with a new one, without losing committed speech.
   * Commit what is left of the current utterance, and wait (limited) for the final transcripts
   * and for running playbook-filling responses of the old session. Then connect the new session.
   * Speech that starts during the switch, about one to three seconds, can be lost.
   */
  private async rotateSession() {
    if (!this.rotationDue) return
    this.rotationDue = false
    this.clearSessionRotationTimer()
    if (!this.canRotate()) return
    await this.commitPendingSpeech()
    await this.waitUntil(() => !this.hasPendingTranscripts() && this.activeResponses === 0)
    // The user can stop during the wait, or the connection can fail and reconnect.
    if (!this.canRotate()) return
    await this.reconnectWebRTC('session rotation')
  }

  /** Rotate only while listening, and not while a stop or a reconnect is running or scheduled. */
  private canRotate() {
    return this.shouldKeepListening && !this.isManualDisconnect && !this.isConnecting && !this.stopPromise && !this.reconnectTimer
  }

  private scheduleReconnect(reason: string) {
    // Only reconnect when the user still wants the session alive and another
    // connection attempt is not already running.
    if (!this.shouldKeepListening || this.isManualDisconnect || this.isConnecting) return
    if (!this.mediaStream || this.mediaStream.getTracks().every(track => track.readyState === 'ended')) {
      void this.handleReconnectFailure(this.params.getErrorMessage('audioUnavailable'))
      return
    }
    if (this.reconnectTimer) return

    this.clearSessionRotationTimer()
    const attempt = this.reconnectAttempts + 1
    if (attempt > MAX_RECONNECT_ATTEMPTS) {
      void this.handleReconnectFailure(this.params.getErrorMessage('connectionLost'))
      return
    }

    // Exponential backoff keeps repeated network failures from hammering the
    // backend/OpenAI while still recovering quickly from brief Wi-Fi drops.
    this.reconnectAttempts = attempt
    const delay = Math.min(RECONNECT_BASE_DELAY_MS * 2 ** (attempt - 1), RECONNECT_MAX_DELAY_MS)
    console.warn(`Realtime session reconnect scheduled (${reason}), attempt ${attempt}/${MAX_RECONNECT_ATTEMPTS}`)
    this.params.onStatusChange('connecting')

    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null
      void this.reconnectWebRTC(reason)
    }, delay)
  }

  private async reconnectWebRTC(reason: string) {
    // Only attempt to reconnect if the user still wants to listen and audio is available
    if (!this.shouldKeepListening || this.isManualDisconnect || !this.mediaStream) return

    try {
      console.info(`Reconnecting Realtime session: ${reason}`)
      this.isConnecting = true
      this.params.onStatusChange('connecting')
      // Reconnect replaces only the OpenAI connection. The browser audio stream
      // stays active so the user does not need to grant mic access again.
      this.cleanupConnection()
      await this.stopStatsCollector()
      await this.connectWebRTC(this.mediaStream)
    } catch (err) {
      console.error('Error reconnecting Realtime session:', err)
      this.isConnecting = false
      this.scheduleReconnect('previous reconnect attempt failed')
    }
  }

  private async handleReconnectFailure(message: string) {
    console.error(message)
    this.params.onError(message)
    await this.stop()
  }

  /** The speaker of a speaker session. Undefined in ordinary transcription and playbook filling. */
  private get speaker() {
    return this.utterances?.speaker
  }

  /**
   * Add the speaker, the utterance, and the connection to an event of a speaker session.
   * The connection ID identifies this controller and connection, so items of an old connection do not mix with new ones.
   */
  private labelSpeakerEvent(event: TeammateRealtimeEvent, utterance: Utterance | undefined): TeammateRealtimeEvent {
    return {
      ...event,
      speaker: this.speaker,
      utterance,
      connectionId: `${this.controllerId}:${this.connectionGeneration}`,
    }
  }

  private buildSessionConfig(): TeammateRealtimeSessionConfig {
    return {
      purpose: this.speaker ? 'transcript' : undefined,
      actionCardSections: this.actionCardSections.map(section => JSON.stringify(section)),
      currentDate: this.getClientLocalDate(),
      recordTranscript: this.params.recordTranscript,
      transcriptionLanguage: this.params.transcriptionLanguage,
      fillAutomatically: this.params.fillAutomatically,
      model: this.params.model,
    }
  }

  private getClientLocalDate() {
    const now = new Date()
    const month = String(now.getMonth() + 1).padStart(2, '0')
    const day = String(now.getDate()).padStart(2, '0')
    return `${now.getFullYear()}-${month}-${day}`
  }

  private startSessionRotationTimer() {
    this.clearSessionRotationTimer()
    // Rotate before the provider-enforced 60-minute cap, so the disconnect happens at a controlled time.
    this.sessionRotationTimer = setTimeout(() => this.requestRotation(), SESSION_ROTATION_MS)
  }

  private clearSessionRotationTimer() {
    this.rotationDue = false
    if (this.sessionRotationTimer) {
      clearTimeout(this.sessionRotationTimer)
      this.sessionRotationTimer = null
    }
  }

  private clearReconnectTimer() {
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer)
      this.reconnectTimer = null
    }
  }

  private cleanupConnection() {
    this.connectionGeneration += 1
    // A speaker session that still waits for its start can no longer become ready.
    this.resolveSessionReady?.(false)

    // Utterances and commits belong to the closed session (the speech detection keeps running on the same audio stream)
    this.needsClientCommits = false
    this.unacknowledgedCommits = 0
    this.pendingTranscriptItemIds.clear()
    this.activeResponses = 0
    this.committedUtterances.length = 0
    this.itemUtterances.clear()
    this.utterances?.setEnabled(false)

    if (this.dataChannel) {
      // Detach handlers before close so intentional cleanup does not schedule
      // a reconnect through stale close/error callbacks.
      this.dataChannel.onopen = null
      this.dataChannel.onclose = null
      this.dataChannel.onerror = null
      this.dataChannel.onmessage = null
      this.dataChannel.close()
      this.dataChannel = null
    }

    if (this.peerConnection) {
      this.peerConnection.close()
      this.peerConnection = null
    }
  }

  private async stopStatsCollector() {
    const statsCollector = this.statsCollector
    if (!statsCollector) return

    this.statsCollector = null
    await statsCollector.stop()
  }
}
